import { useCallback, useMemo, useState } from 'react';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import RULToolView from './RULToolView';
import { TracesViewAdvanced } from './TracesViewAdvanced';
import { ToolSummaryCardsSkeleton } from './ToolSummaryCards';
import { ToolMetadataCard } from './ToolMetadataCard';
import { ToolDataPanel } from './ToolDataPanel';
import { computeTraceStats } from '../utils/computeTraceStats';
import { Card } from '@/components/ui/card';
import type { Tool } from '@/features/traceability/type/Tool';

export type ToolViewContentProps = {
  toolId: string;
  traceabilityApiClient: TraceabilityApiClient;
  /** Open the reference editor on load (`?edit=reference`). */
  editReference?: boolean;
};

export function ToolViewContent({
  toolId,
  traceabilityApiClient,
  editReference = false,
}: ToolViewContentProps) {
  const [currentTool, setCurrentTool] = useState<Tool | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const handleToolLoad = useCallback((tool: Tool | null) => {
    setCurrentTool(tool);
    setIsLoading(false);
  }, []);

  const handleReferenceChange = useCallback((reference: string) => {
    setCurrentTool((tool) => (tool ? { ...tool, reference } : tool));
  }, []);

  const stats = useMemo(
    () => (currentTool ? computeTraceStats(currentTool.traces) : null),
    [currentTool]
  );

  return (
    <div className="flex flex-col space-y-8">
      <RULToolView key={`rul-${toolId}`} className="w-full" toolId={toolId} />

      {stats && currentTool && (
        <Card padding="lg" className="flex flex-col gap-4">
          <ToolMetadataCard
            tool={currentTool}
            lastTraceToolState={
              currentTool.traces.length > 0
                ? currentTool.traces[currentTool.traces.length - 1].toolState
                : null
            }
            topMachine={stats.machineUsage[0]?.[0] ?? null}
            onReferenceChange={handleReferenceChange}
            startEditingReference={editReference}
          />
          <ToolDataPanel stats={stats} />
        </Card>
      )}

      {!stats && isLoading && <ToolSummaryCardsSkeleton />}

      <TracesViewAdvanced
        key={`traces-advanced-${toolId}`}
        traceabilityApiClient={traceabilityApiClient}
        toolId={toolId}
        onToolLoad={handleToolLoad}
      />
    </div>
  );
}
